import type { Ref } from 'vue';
import {
  computed,
} from 'vue';

import type { ITableMultiselectActionParams } from '@modules/tables/components/VTable/types';
import type {
  ITableMultiselectActions,
  ITableState,
} from '@modules/tables/types';
import {
  AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
} from '@modules/affiliate-program-creatives/constants';
import {
  useActivateCreativeMutation,
  useArchiveCreativeMutation,
  useDeactivateCreativeMutation,
  useUnarchiveCreativeMutation,
} from '@modules/affiliate-program-creatives/mutations';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';

export interface AffiliateProgramCreativesListMultipleActionsComposable {
  multiSelectOptions: Ref<ITableMultiselectActions>;
  onMultiselectAction(params: ITableMultiselectActionParams): void;
}

export default function useAffiliateProgramCreativesListMultipleActions(
  tableState: Ref<ITableState>,
  type: Ref<string>,
): AffiliateProgramCreativesListMultipleActionsComposable {
  const { hasPermission } = usePermissionsStore();
  const { getTranslation } = useI18n();
  const { confirmEdit } = useConfirmDialogs();

  const { mutateAsync: activateCreatives } = useActivateCreativeMutation();
  const { mutateAsync: deactivateCreatives } = useDeactivateCreativeMutation();
  const { mutateAsync: archiveCreatives } = useArchiveCreativeMutation();
  const { mutateAsync: unarchiveCreatives } = useUnarchiveCreativeMutation();

  const multiSelectOptions = computed<ITableMultiselectActions>(() => {
    const actions: ITableMultiselectActions = {};

    if (hasPermission(AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.ACTIVATE)) {
      actions.activate = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'SET_ACTIVATED').value,
      };

      actions.deactivate = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'SET_UNACTIVATED').value,
      };
    }

    if (hasPermission(AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.ARCHIVE)) {
      actions.archive = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'ARCHIVE').value,
      };

      actions.unarchive = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'UNARCHIVE').value,
      };
    }

    return actions;
  });

  function onMultiselectAction(params: ITableMultiselectActionParams): void {
    const modalMessage = getTranslation(
      TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
      params.selection.length ? 'LIST_OF_CREATIVE_IDS' : 'NO_CREATIVE_CHOSEN',
      {
        ids: params.selection.join(', '),
      },
    ).value;

    switch (params.actionId) {
      case 'activate':
        confirmEdit({
          header: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'SET_ACTIVE_TITLE').value,
          message: modalMessage,
          accept: () => {
            void activateCreatives({
              ids: params.selection,
              segment_id: tableState.value.filter_set_id,
              format: type.value,
              page: tableState.value.page,
            });
          },
        });
        break;
      case 'deactivate':
        confirmEdit({
          header: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'SET_UNACTIVE_TITLE').value,
          message: modalMessage,
          accept: () => {
            void deactivateCreatives({
              ids: params.selection,
              segment_id: tableState.value.filter_set_id,
              format: type.value,
              page: tableState.value.page,
            });
          },
        });
        break;
      case 'archive':
        void archiveCreatives({
          ids: params.selection,
          segment_id: tableState.value.filter_set_id,
          format: type.value,
          page: tableState.value.page,
        });
        break;
      case 'unarchive':
        void unarchiveCreatives({
          ids: params.selection,
          segment_id: tableState.value.filter_set_id,
          format: type.value,
          page: tableState.value.page,
        });
        break;
      default:
        break;
    }
  }

  return {
    multiSelectOptions,
    onMultiselectAction,
  };
}
